import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Input,
  Label,
  Button,
  Accordion,
  AccordionItem,
  TabContent,
  TabPane,
  Nav,
  NavItem,
  NavLink,
} from "../../Components/Common/Tailwind";

import { cn } from "@/Components/ui/utils";
import { API } from "../../api";
import Navbar from "../../Components/Common/navbar";
import QuantityButtonInput from "../../Components/Common/QuantityButtonInput";
import MobileViewContainer from "../../Components/MobileViewContainer";
import { useUserStore } from "../../store/store";
import { Link } from "react-router-dom";
import LoginPromptModal from "../../Components/modal/LoginPromptModal";
import { ChevronLeft } from "lucide-react";

const ImageCarousel = ({ items }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (items.length <= 1) return;
    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % items.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [items.length]);

  if (!items || items.length === 0) return null;

  return (
    <div className="relative w-full aspect-square overflow-hidden bg-gray-100 rounded-2xl group">
      <div
        className="flex transition-transform duration-500 ease-in-out h-full"
        style={{ transform: `translateX(-${currentIndex * 100}%)` }}
      >
        {items.map((item, index) => (
          <div key={index} className="w-full h-full flex-shrink-0">
            <img
              src={item.src}
              alt={`Product image ${index + 1}`}
              className="w-full h-full object-cover"
            />
          </div>
        ))}
      </div>

      {items.length > 1 && (
        <>
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5 px-3 py-1.5 bg-black/20 backdrop-blur-md rounded-full">
            {items.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={cn(
                  "w-1.5 h-1.5 rounded-full transition-all",
                  currentIndex === index ? "bg-white w-4" : "bg-white/50"
                )}
              />
            ))}
          </div>

          <button
            onClick={() => setCurrentIndex((prev) => (prev - 1 + items.length) % items.length)}
            className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-white/80 backdrop-blur-sm rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
          </button>
          <button
            onClick={() => setCurrentIndex((prev) => (prev + 1) % items.length)}
            className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-white/80 backdrop-blur-sm rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
          </button>
        </>
      )}
    </div>
  );
};

function ShoppingDetail(props) {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const [user] = useUserStore((state) => [state.user]);

  const [product, setProduct] = useState(location.state?.product);
  const [quantity, setQuantity] = useState(1);
  const [selectOptions, setSelectOptions] = useState([]);
  const [showLoginModal, setShowLoginModal] = useState(false);

  const [customActiveTab, setcustomActiveTab] = useState("1");
  const toggleCustom = (tab) => {
    if (customActiveTab !== tab) {
      setcustomActiveTab(tab);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await API.get(`/shop/product/${id}/`);
        const data = response.data;
        setProduct(data);
      } catch (error) {
        console.error(error);
      }
    };

    fetchData();
  }, [id]);

  useEffect(() => {
    if (product?.quantity) setQuantity(product.quantity);
  }, [product]);

  const handleSelectOption = (checked, id, price, value, quantity, is_required) => {
    if (checked) {
      setSelectOptions([...selectOptions, { id: id, price: price, value: value, quantity: quantity, is_required: is_required }]);
    } else {
      setSelectOptions(selectOptions.filter((option) => option.id !== id));
    }
  };

  return (
    <MobileViewContainer>

      <div className="back-to-list mt-4 px-2 mb-4">
        <Link to="/shop" className="flex items-center text-lg font-bold text-black no-underline">
          <ChevronLeft size={24} className="mr-1" /> 목록으로
        </Link>
      </div>

      <Container className="min-h-screen pb-40">
        <Row>
          <Col className="p-0">
            <Card className="border-0 bg-transparent">
              <CardBody className="p-0">
                <div className="max-w-[640px] mx-auto overflow-x-hidden">
                  {product?.images?.length ? (
                    <ImageCarousel
                      items={product.images.map((img) => ({
                        src: `${img.image}`,
                        key: img.id,
                      }))}
                    />
                  ) : (
                    <div className="w-full aspect-square bg-gray-100 flex items-center justify-center rounded-2xl overflow-hidden shadow-sm">
                      <img
                        src={`${product?.thumbnail}`}
                        className="w-full h-full object-cover"
                        alt={product?.name}
                      />
                    </div>
                  )}
                </div>

                {/* Product Info Section */}
                <div className="px-5 mt-6">
                  <div className="flex flex-col gap-1">
                    <h3 className="text-2xl font-bold tracking-tight text-gray-900" style={{ fontFamily: "Gmarket-B" }}>
                      {product?.name}
                    </h3>

                    <div className="flex items-baseline gap-2.5 mt-2">
                      {product?.no_sale_price > product?.price && (
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-bold text-[#F6475F]">
                            {Math.round(product.sale_rate)}%
                          </span>
                          <span className="text-sm text-gray-400 line-through decoration-gray-300">
                            {new Intl.NumberFormat().format(product.no_sale_price)}원
                          </span>
                        </div>
                      )}
                      <span className="text-[26px] font-bold text-gray-900" style={{ fontFamily: "Gmarket-B" }}>
                        {new Intl.NumberFormat().format(product?.price)}
                        <span className="text-lg ml-0.5">원</span>
                      </span>
                    </div>
                  </div>

                  {/* Variations Section */}
                  {product?.variations?.length > 0 && (
                    <div className="mt-8">
                      <h4 className="text-[15px] font-bold text-gray-800 mb-4" style={{ fontFamily: "Gmarket-M" }}>
                        옵션 선택
                      </h4>
                      <Accordion className="space-y-3">
                        {product.variations.map((variation, idx) => (
                          <AccordionItem
                            key={idx}
                            title={
                              <span className="text-[14px] font-semibold text-gray-700">
                                {variation.is_required ? (
                                  <span className="text-[#F6475F] mr-1">*</span>
                                ) : null}
                                {variation.name}
                              </span>
                            }
                            className="!border-0 !rounded-xl !bg-gray-50/80 !shadow-sm overflow-hidden"
                          >
                            <div className="flex flex-col gap-2.5">
                              {variation.items.map((optionItem) => (
                                <div
                                  key={optionItem.id}
                                  className={cn(
                                    "flex items-center justify-between p-4 rounded-xl border-2 transition-all cursor-pointer",
                                    selectOptions.some(o => o.id === optionItem.id)
                                      ? "bg-white border-[#F6475F] shadow-md shadow-[#F6475F]/5"
                                      : "bg-white border-transparent hover:border-gray-100"
                                  )}
                                  onClick={() => {
                                    const isChecked = selectOptions.some(o => o.id === optionItem.id);
                                    handleSelectOption(!isChecked, optionItem.id, optionItem.price, optionItem.value, 1, variation.is_required);
                                  }}
                                >
                                  <div className="flex items-center gap-3">
                                    <div className={cn(
                                      "w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors",
                                      selectOptions.some(o => o.id === optionItem.id)
                                        ? "bg-[#F6475F] border-[#F6475F]"
                                        : "border-gray-200"
                                    )}>
                                      {selectOptions.some(o => o.id === optionItem.id) && (
                                        <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                          <path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" />
                                        </svg>
                                      )}
                                    </div>
                                    <div>
                                      <div className="text-[14px] font-medium text-gray-800">{optionItem.value}</div>
                                      <div className="text-[12px] text-gray-500 font-medium">
                                        {optionItem.price > 0 ? `+${optionItem.price.toLocaleString()}원` : "추가 금액 없음"}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </AccordionItem>
                        ))}
                      </Accordion>
                    </div>
                  )}

                  {/* Content Tabs */}
                  <div className="mt-12">
                    <Nav className="flex flex-row border-b border-gray-100">
                      {[
                        { id: "1", label: "상세정보" },
                        { id: "2", label: "제공고시" },
                        { id: "3", label: "요약정보" },
                      ].map((tab) => (
                        <NavItem key={tab.id} className="flex-1">
                          <NavLink
                            className={cn(
                              "relative py-4 text-[15px] font-bold text-center transition-colors cursor-pointer block w-full",
                              customActiveTab === tab.id ? "text-gray-900" : "text-gray-400 hover:text-gray-600"
                            )}
                            onClick={() => toggleCustom(tab.id)}
                          >
                            {tab.label}
                            {customActiveTab === tab.id && (
                              <div className="absolute bottom-0 left-0 w-full h-1 bg-[#F6475F] rounded-t-full shadow-[0_-2px_6px_rgba(246,71,95,0.2)]" />
                            )}
                          </NavLink>
                        </NavItem>
                      ))}
                    </Nav>

                    <TabContent activeTab={customActiveTab} className="py-8">
                      <TabPane tabId="1" className="animate-in fade-in duration-500">
                        {product?.detail_img && (
                          <img src={`${product.detail_img}`} className="w-full rounded-2xl shadow-sm" alt="detail" />
                        )}
                      </TabPane>
                      <TabPane tabId="2">
                        <div className="rounded-2xl border border-gray-100 overflow-hidden shadow-sm">
                          {product?.info_notice_list?.map((item, idx) => (
                            <div key={idx} className="flex border-b last:border-0 border-gray-50 bg-white">
                              <div className="w-[120px] bg-gray-50/80 p-4 text-[12px] font-bold text-gray-600 flex items-center">
                                {item.key}
                              </div>
                              <div className="flex-1 p-4 text-[12px] text-gray-800 leading-relaxed font-medium">
                                {item.value}
                              </div>
                            </div>
                          ))}
                        </div>
                      </TabPane>
                      <TabPane tabId="3">
                        <div
                          className="text-[14px] leading-[1.8] text-gray-700 font-medium px-1"
                          dangerouslySetInnerHTML={{ __html: product?.descript }}
                        />
                      </TabPane>
                    </TabContent>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Sticky Bottom Bar */}
        <div className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-[640px] z-[100] px-4 pb-8 pt-2 bg-gradient-to-t from-white via-white to-transparent">
          <div className="bg-white rounded-[24px] border border-gray-100 shadow-[0_-10px_40px_rgba(0,0,0,0.1)] p-4 flex flex-col gap-4">
            {/* Selected Options Summary */}
            {selectOptions.length > 0 && (
              <div className="max-h-52 overflow-y-auto space-y-2 pr-1 custom-scrollbar">
                {selectOptions.map((option, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3.5 bg-gray-50 rounded-[16px] border border-gray-100/50">
                    <div className="flex-1 mr-4 min-w-0">
                      <div className="text-[13px] font-bold text-gray-800 truncate mb-1">{option.value}</div>
                      <div className="text-[12px] text-gray-500 font-bold">
                        {option.price > 0 ? `+${option.price.toLocaleString()}원` : "추가 금액 없음"}
                      </div>
                    </div>
                    <QuantityButtonInput
                      size={24}
                      quantity={option.quantity}
                      onChange={(value) => {
                        if (value < 1) return;
                        const newOptions = [...selectOptions];
                        newOptions[idx].quantity = value;
                        setSelectOptions(newOptions);
                      }}
                    />
                  </div>
                ))}
              </div>
            )}

            <div className="flex items-center gap-3">
              {!product?.variations?.length && product?.remaining_stock > 0 && (
                <div className="flex-shrink-0 bg-gray-50 p-2 rounded-xl border border-gray-100">
                  <QuantityButtonInput
                    size={28}
                    quantity={quantity}
                    onChange={(value) => setQuantity(value)}
                  />
                </div>
              )}

              <Button
                className={cn(
                  "flex-1 py-[18px] text-[17px] font-bold rounded-[18px] border-0 transition-all active:scale-[0.98]",
                  product?.remaining_stock <= 0
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-black text-white shadow-lg active:bg-gray-900"
                )}
                style={{ fontFamily: "Gmarket-M" }}
                disabled={product?.remaining_stock <= 0}
                onClick={() => {
                  if (!user) {
                    setShowLoginModal(true);
                    return;
                  }

                  if (product.variations?.length > 0 && selectOptions.length === 0) {
                    toast.warning("옵션을 선택해주세요.");
                    return;
                  }

                  navigate(`/order-shop`, {
                    state: {
                      products: [{
                        ...product,
                        quantity: product.variations?.length > 0 ? 1 : quantity,
                        selectVariation: selectOptions
                      }],
                    },
                  });
                }}
              >
                {product?.remaining_stock <= 0 ? "품절된 상품입니다" : "결제하기"}
              </Button>
            </div>
          </div>
        </div>
      </Container>
      <LoginPromptModal isOpen={showLoginModal} onClose={() => setShowLoginModal(false)} />
    </MobileViewContainer >
  );
}

export default ShoppingDetail;

